/**
 * Professional WordPress Header Navigation
 *
 * Handles responsive navigation with proper accessibility and mobile menu functionality
 */

(function() {
    'use strict';
    
    // Configuration
    const BREAKPOINT_SM = 1024; 
    const SCROLL_THRESHOLD = 100; // Minimum scroll distance to trigger hide/show
    const SCROLL_THROTTLE = 16; // Throttle scroll events (~60fps)
    
    // State
    let isMobileMenuOpen = false;
    let currentBreakpoint = getCurrentBreakpoint();
    
    // Scroll state
    let lastScrollY = 0;
    let isScrollingDown = false;
    let isHeaderHidden = false;
    let scrollTimer = null;
    
    // DOM Elements
    let elements = {};
    
    // Initialize when DOM is ready
    document.addEventListener('DOMContentLoaded', function() {
        initElements();
        initMobileMenu();
        initDropdowns();
        initResponsiveBehavior();
        initStickyHeader();
    });
    
    /**
     * Cache DOM elements
     */
    function initElements() {
        elements = {
            header: document.querySelector('.site-header'),
            mobileToggle: document.querySelector('.mobile-menu-toggle'),
            mobileNav: document.querySelector('.mobile-navigation'),
            desktopNav: document.querySelector('.desktop-navigation'),
            body: document.body
        };
    }
    
    /**
     * Initialize mobile menu functionality
     */
    function initMobileMenu() {
        if (!elements.mobileToggle || !elements.mobileNav) {
            return;
        }
        
        // Toggle menu on button click
        elements.mobileToggle.addEventListener('click', function(e) {
            e.preventDefault();
            toggleMobileMenu();
        });
        
        
        // Close menu when clicking on navigation links (but not dropdown toggles)
        const mobileLinks = elements.mobileNav.querySelectorAll('a');
        mobileLinks.forEach(function(link) {
            link.addEventListener('click', function() {
                // Don't close menu if this is a dropdown toggle
                if (!this.parentElement.classList.contains('menu-item-has-children') && isMobileMenuOpen) {
                    closeMobileMenu();
                }
            });
        });
        
        // Close menu on escape key
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape' && isMobileMenuOpen) {
                closeMobileMenu();
            }
        });
        
        // Close menu when clicking outside
        document.addEventListener('click', function(e) {
            if (isMobileMenuOpen && 
                !elements.mobileNav.contains(e.target) && 
                !elements.mobileToggle.contains(e.target)) {
                closeMobileMenu();
            }
        });
    }
    
    /**
     * Initialize dropdown functionality for both desktop and mobile
     */
    function initDropdowns() {
        // Mobile dropdown toggles - click on entire menu item
        const mobileDropdownLinks = document.querySelectorAll('.mobile-navigation .menu-item-has-children > a');
        
        mobileDropdownLinks.forEach(function(link) {
            link.addEventListener('click', function(e) {
                if (getCurrentBreakpoint() === 'mobile') {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleDropdown(this.closest('.menu-item-has-children'));
                }
            });
        });
        
        // Desktop dropdown toggles - click on entire menu item
        const desktopDropdownLinks = document.querySelectorAll('.desktop-navigation .menu-item-has-children > a');
        
        desktopDropdownLinks.forEach(function(link) {
            link.addEventListener('click', function(e) {
                if (getCurrentBreakpoint() === 'desktop') {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleDesktopDropdown(this.closest('.menu-item-has-children'));
                }
            });
        });
        
        // Close desktop dropdowns when clicking outside
        document.addEventListener('click', function(e) {
            if (getCurrentBreakpoint() === 'desktop') {
                const openDesktopDropdowns = document.querySelectorAll('.desktop-navigation .menu-item-has-children.is-open');
                openDesktopDropdowns.forEach(function(dropdown) {
                    if (!dropdown.contains(e.target)) {
                        dropdown.classList.remove('is-open');
                    }
                });
            }
        });
    }
    
    /**
     * Toggle mobile menu
     */
    function toggleMobileMenu() {
        if (isMobileMenuOpen) {
            closeMobileMenu();
        } else {
            openMobileMenu();
        }
    }
    
    /**
     * Open mobile menu
     */
    function openMobileMenu() {
        isMobileMenuOpen = true;
        
        // Update classes
        elements.mobileToggle.classList.add('is-active');
        elements.mobileNav.classList.add('is-open');
        elements.body.classList.add('mobile-menu-open');
        
        // Update ARIA attributes
        elements.mobileToggle.setAttribute('aria-expanded', 'true');
        elements.mobileNav.setAttribute('aria-hidden', 'false');
        
        // Focus management
        setTimeout(function() {
            const firstLink = elements.mobileNav.querySelector('a');
            if (firstLink) {
                firstLink.focus();
            }
        }, 100);
    }
    
    /**
     * Close mobile menu
     */
    function closeMobileMenu() {
        isMobileMenuOpen = false;
        
        // Update classes
        elements.mobileToggle.classList.remove('is-active');
        elements.mobileNav.classList.remove('is-open');
        elements.body.classList.remove('mobile-menu-open');
        
        // Update ARIA attributes
        elements.mobileToggle.setAttribute('aria-expanded', 'false');
        elements.mobileNav.setAttribute('aria-hidden', 'true');
        
        // Close all mobile dropdowns
        const openMobileDropdowns = elements.mobileNav.querySelectorAll('.menu-item-has-children.is-open');
        openMobileDropdowns.forEach(function(dropdown) {
            dropdown.classList.remove('is-open');
            const subMenu = dropdown.querySelector('.sub-menu');
            if (subMenu) {
                subMenu.classList.remove('is-open');
            }
        });
        
        // Close all desktop dropdowns
        const openDesktopDropdowns = document.querySelectorAll('.desktop-navigation .menu-item-has-children.is-open');
        openDesktopDropdowns.forEach(function(dropdown) {
            dropdown.classList.remove('is-open');
        });
        
        // Return focus to toggle button
        elements.mobileToggle.focus();
    }
    
    /**
     * Toggle dropdown in mobile menu
     */
    function toggleDropdown(menuItem) {
        const isOpen = menuItem.classList.contains('is-open');
        const subMenu = menuItem.querySelector('.sub-menu');
        
        if (!subMenu) return;
        
        if (isOpen) {
            menuItem.classList.remove('is-open');
            subMenu.classList.remove('is-open');
        } else {
            menuItem.classList.add('is-open');
            subMenu.classList.add('is-open');
        }
    }
    
    /**
     * Toggle dropdown in desktop menu
     */
    function toggleDesktopDropdown(menuItem) {
        const isOpen = menuItem.classList.contains('is-open');
        
        // Close all other open dropdowns first
        const allDropdowns = document.querySelectorAll('.desktop-navigation .menu-item-has-children.is-open');
        allDropdowns.forEach(function(dropdown) {
            if (dropdown !== menuItem) {
                dropdown.classList.remove('is-open');
            }
        });
        
        // Toggle current dropdown
        if (isOpen) {
            menuItem.classList.remove('is-open');
        } else {
            menuItem.classList.add('is-open');
        }
    }
    
    /**
     * Handle responsive behavior
     */
    function initResponsiveBehavior() {
        function handleResize() {
            const newBreakpoint = getCurrentBreakpoint();
            
            // If switching from mobile to desktop, close mobile menu
            if (currentBreakpoint === 'mobile' && newBreakpoint === 'desktop' && isMobileMenuOpen) {
                closeMobileMenu();
            }
            
            currentBreakpoint = newBreakpoint;
        }
        
        // Initial call
        handleResize();
        
        // Listen for resize events (debounced)
        let resizeTimeout;
        window.addEventListener('resize', function() {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(handleResize, 150);
        });
    }
    
    /**
     * Initialize sticky header functionality
     */
    function initStickyHeader() {
        if (!elements.header) {
            return;
        }
        
        // Set initial state
        lastScrollY = window.scrollY;
        
        // Add scroll event listener with throttling
        window.addEventListener('scroll', throttleScroll);
        
        // Initial header state
        updateHeaderState();
    }
    
    /**
     * Throttle scroll events for performance
     */
    function throttleScroll() {
        if (scrollTimer) {
            return;
        }
        
        scrollTimer = setTimeout(function() {
            handleScroll();
            scrollTimer = null;
        }, SCROLL_THROTTLE);
    }
    
    /**
     * Handle scroll events and update header state
     */
    function handleScroll() {
        const currentScrollY = window.scrollY;
        const scrollDifference = Math.abs(currentScrollY - lastScrollY);
        
        // Only trigger state changes if scroll difference exceeds threshold
        if (scrollDifference < 10) {
            return;
        }
        
        // Determine scroll direction
        isScrollingDown = currentScrollY > lastScrollY;
        
        // Update last scroll position
        lastScrollY = currentScrollY;
        
        // Update header state
        updateHeaderState();
    }
    
    /**
     * Update header visibility based on scroll position and direction
     */
    function updateHeaderState() {
        const currentScrollY = window.scrollY;
        
        // Always show header when at top of page
        if (currentScrollY <= SCROLL_THRESHOLD) {
            showHeader();
            return;
        }
        
        // Hide header when scrolling down, show when scrolling up
        if (isScrollingDown && !isHeaderHidden) {
            hideHeader();
        } else if (!isScrollingDown && isHeaderHidden) {
            showHeader();
        }
    }
    
    /**
     * Hide the header with animation
     */
    function hideHeader() {
        if (!elements.header || isHeaderHidden) {
            return;
        }
        
        elements.header.classList.add('site-header--hidden');
        elements.header.classList.remove('site-header--visible');
        isHeaderHidden = true;
    }
    
    /**
     * Show the header with animation
     */
    function showHeader() {
        if (!elements.header || !isHeaderHidden) {
            return;
        }
        
        elements.header.classList.add('site-header--visible');
        elements.header.classList.remove('site-header--hidden');
        isHeaderHidden = false;
    }
    
    /**
     * Get current breakpoint
     */
    function getCurrentBreakpoint() {
        return window.innerWidth < BREAKPOINT_SM ? 'mobile' : 'desktop';
    }
    
})();